/**
 * Grid export registry — the single place an agent's export request reaches a
 * results grid.
 *
 * WHY A SHARED REGISTRY. The app has TWO results grids (see CLAUDE.md, "Two
 * Results Grids"): `common/components/ResultsGrid.tsx` for the Query Editor /
 * Notebook / FK preview, and `table-explorer/components/TableExplorerGrid.tsx`
 * driven from `TableExplorerTab.tsx`. Their export menus, and the handlers
 * behind them, are independent — which is exactly the drift the project rule
 * warns about. Rather than teaching `ui_export_results` about each grid (two
 * code paths that would silently diverge the first time one grid grows a
 * format), each grid REGISTERS the export handler its own menu already calls.
 * The MCP path therefore runs the user-facing export verbatim: same identity
 * handling, same spatial WKT decoding, same Personal-mode row limit, same
 * toasts, same analytics.
 *
 * A plain Map rather than a zustand store on purpose: nothing re-renders off
 * this, the only reader is a one-shot MCP command handler, and a store would
 * add a subscription nobody needs.
 */

import type { ResultsExportFormat } from '../../../../types/resultsExport';

export type GridExportOutcome =
  | { success: true; filePath: string; rowCount?: number }
  | { success: false; error: string; cancelled?: boolean };

export type GridExportScope = 'active' | 'all';

export type GridExportRunner = {
  /**
   * Export the rows the grid currently holds, in any of the five formats the
   * grid's own export menu offers. `scope: 'all'` means every result set in
   * the tab (Query Editor only — Table Explorer has exactly one and ignores
   * it).
   */
  exportLoaded: (args: {
    format: ResultsExportFormat;
    scope: GridExportScope;
  }) => Promise<GridExportOutcome>;
  /**
   * Stream EVERY row to CSV, past whatever the grid has buffered — the "Export
   * All to CSV" entry. Present only on windowed Query Editor result sets;
   * Table Explorer paginates and has no such path, so it leaves this undefined
   * and the tool says so rather than quietly exporting one page.
   */
  exportAllCsv?: () => Promise<GridExportOutcome>;
  /** Rows the grid could export right now. Zero means "nothing loaded yet". */
  loadedRowCount: () => number;
};

const runners = new Map<string, GridExportRunner>();

/** Called by a grid on mount; the returned function unregisters it. */
export function registerGridExport(tabId: string, runner: GridExportRunner): () => void {
  runners.set(tabId, runner);
  return () => {
    // Only clear the slot if it is still ours: a remount can register the new
    // runner before the old effect's cleanup runs.
    if (runners.get(tabId) === runner) runners.delete(tabId);
  };
}

export function getGridExportRunner(tabId: string): GridExportRunner | undefined {
  return runners.get(tabId);
}

/** Test-only: drop every registration so specs start from a clean registry. */
export function resetGridExportRegistry(): void {
  runners.clear();
}
